/**
 * Edit Admin User Page
 * Edit an existing administrator account
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  LinearProgress,
  Breadcrumbs,
  Link,
  Divider,
  Button,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../components/common';
import { Card, CardContent, TextField } from '../../components/ui';
import { AdminUserService } from '../../services/adminUser/AdminUserService';
import { canManageAdminUsers } from '../../utils/permissions';
import type { AdminUser, AdminUserRole } from '../../types/adminUser';
import { adminUserRoleLabels } from '../../types/adminUser';

const calculatePasswordStrength = (password: string): { strength: 'weak' | 'medium' | 'strong'; value: number } => {
  if (!password) {
    return { strength: 'weak', value: 0 };
  }

  let strength = 0;

  // Length check
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;

  // Character variety checks
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;

  // Bonus for longer passwords
  if (password.length >= 16) strength += 10;

  if (strength < 40) {
    return { strength: 'weak', value: strength };
  } else if (strength < 70) {
    return { strength: 'medium', value: strength };
  } else {
    return { strength: 'strong', value: strength };
  }
};

const getPasswordStrengthColor = (strength: 'weak' | 'medium' | 'strong', theme: Theme): string => {
  switch (strength) {
    case 'weak':
      return theme.palette.error.main;
    case 'medium':
      return theme.palette.warning.main;
    case 'strong':
      return theme.palette.success.main;
    default:
      return theme.palette.text.secondary;
  }
};

const EditAdminUserPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('mode') === 'view';
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminUserRole>('support');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const passwordStrength = calculatePasswordStrength(newPassword);

  // Check permission
  useEffect(() => {
    if (!canManageAdminUsers()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fetchAdminUser = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const user = await AdminUserService.getAdminUserById(id);
        if (user) {
          setAdminUser(user);
          // Split name into first and last name
          const nameParts = (user.name || '').split(' ');
          setFirstName(nameParts[0] || '');
          setLastName(nameParts.slice(1).join(' ') || '');
          setEmail(user.email);
          setRole(user.role);
        }
      } catch (error) {
        console.error('Failed to fetch admin user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminUser();
  }, [id]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    const trimmedEmail = email.trim();

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // If either password field is filled, both are required and must match
    if (newPassword || confirmPassword) {
      if (!newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters long';
      }
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your new password';
      } else if (newPassword !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !adminUser) {
      return;
    }

    try {
      setIsSubmitting(true);
      const updateData: {
        firstName?: string;
        lastName?: string;
        email?: string;
        role?: AdminUserRole;
        password?: string;
      } = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        role,
      };

      // Only include password if new password is provided
      if (newPassword) {
        updateData.password = newPassword;
      }

      await AdminUserService.updateAdminUser(adminUser.id, updateData);
      // Navigate back to admin users list
      navigate('/settings/admin-users');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update admin user';
      setErrors({ email: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings/admin-users');
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!adminUser) {
    return (
      <MainLayout>
        <EmptyState
          title="Admin User Not Found"
          message="The admin user you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(3),
        }}
      >
        {isViewMode ? 'View Administrator' : 'Edit Administrator'}: {adminUser.name}
      </Typography>

      {/* Form Card */}
      <Card>
        <CardContent>
          {/* Personal Information */}
          <Box sx={{ marginBottom: theme.spacing(4) }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: theme.typography.body1.fontSize,
              }}
            >
              Personal Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) {
                      setErrors({ ...errors, firstName: undefined });
                    }
                  }}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  fullWidth
                  disabled={isViewMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                          borderStyle: 'dashed',
                        },
                        '& .MuiInputBase-input': {
                          color: theme.palette.text.secondary,
                          WebkitTextFillColor: theme.palette.text.secondary,
                          cursor: 'default',
                        },
                        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                          color: theme.palette.text.disabled,
                        }
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName) {
                      setErrors({ ...errors, lastName: undefined });
                    }
                  }}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  fullWidth
                  disabled={isViewMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                          borderStyle: 'dashed',
                        },
                        '& .MuiInputBase-input': {
                          color: theme.palette.text.secondary,
                          WebkitTextFillColor: theme.palette.text.secondary,
                          cursor: 'default',
                        },
                        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                          color: theme.palette.text.disabled,
                        }
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors({ ...errors, email: undefined });
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email || (!isViewMode && email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Valid email format' : '')}
                  fullWidth
                  disabled={isViewMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                          borderStyle: 'dashed',
                        },
                        '& .MuiInputBase-input': {
                          color: theme.palette.text.secondary,
                          WebkitTextFillColor: theme.palette.text.secondary,
                          cursor: 'default',
                        },
                        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                          color: theme.palette.text.disabled,
                        }
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      color: !isViewMode && email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? theme.palette.success.main : undefined,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ marginBottom: theme.spacing(4) }} />

          {/* Role & Permissions */}
          <Box sx={{ marginBottom: theme.spacing(4) }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: theme.typography.body1.fontSize,
              }}
            >
              Role & Permissions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as AdminUserRole)}
                  fullWidth
                  disabled={isViewMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdminPanelSettingsIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                          borderStyle: 'dashed',
                        },
                        '& .MuiInputBase-input': {
                          color: theme.palette.text.secondary,
                          WebkitTextFillColor: theme.palette.text.secondary,
                          cursor: 'default',
                        },
                        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                          color: theme.palette.text.disabled,
                        },
                        '& .MuiSelect-icon': {
                          color: theme.palette.text.disabled,
                        }
                      },
                    },
                  }}
                >
                  <MenuItem value="super_admin">{adminUserRoleLabels.super_admin}</MenuItem>
                  <MenuItem value="support">{adminUserRoleLabels.support}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ marginBottom: theme.spacing(4) }} />

          {/* Change Password - Hidden in view mode */}
          {!isViewMode && (
            <Box sx={{ marginBottom: theme.spacing(4) }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: theme.typography.body1.fontSize,
                }}
              >
                Change Password
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="New Password"
                    placeholder="Enter new password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword) {
                        setErrors({ ...errors, newPassword: undefined });
                      }
                    }}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm New Password"
                    placeholder="Re-enter new password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors({ ...errors, confirmPassword: undefined });
                      }
                    }}
                    error={!!errors.confirmPassword || (!!confirmPassword && !!newPassword && newPassword !== confirmPassword)}
                    helperText={errors.confirmPassword || (confirmPassword && newPassword && newPassword !== confirmPassword ? 'Passwords do not match' : '')}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                {newPassword && (
                  <Grid item xs={12}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: theme.typography.caption.fontSize,
                          }}
                        >
                          Password Strength
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: getPasswordStrengthColor(passwordStrength.strength, theme),
                            fontSize: theme.typography.caption.fontSize,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        >
                          {passwordStrength.strength}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength.value}
                        sx={{
                          height: 6,
                          borderRadius: theme.shapes.borderRadius.md,
                          backgroundColor: theme.customColors.searchBackground,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getPasswordStrengthColor(passwordStrength.strength, theme),
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          marginTop: theme.spacing(0.5),
                          display: 'block',
                        }}
                      >
                        Use a mix of letters, numbers, and symbols for a strong password.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing(2),
              marginTop: theme.spacing(4),
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                padding: theme.spacing(1, 3),
                borderColor: theme.customColors.tableBorder,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.customColors.tableRowHover,
                },
              }}
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  padding: theme.spacing(1, 3),
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default EditAdminUserPage;
